import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { fetchLookbookOwners } from '@app/services/lookbookRepository';
import { useDebouncedCallback } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';

const DEBOUNCE_TIME = 500;
const LOOKBOOK_OWNERS_QUERY_KEY = 'lookbookOwners';

export const useLookbookOwners = () => {
  const [search, setSearch] = React.useState('');

  const handleFetchLookbookOwners = async ({ pageParam }: { pageParam: number }) => {
    return fetchLookbookOwners({
      limit: DEFAULT_ITEMS_COUNT_PER_REQUEST,
      name: search,
      page: pageParam,
    });
  };

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    gcTime: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!isDefined(lastPage) || lastPage.totalCount <= lastPage.page * DEFAULT_ITEMS_COUNT_PER_REQUEST) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    initialPageParam: 1,
    queryFn: handleFetchLookbookOwners,
    queryKey: [LOOKBOOK_OWNERS_QUERY_KEY, { search }],
  });

  const handleSearchChange = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, DEBOUNCE_TIME);

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const owners = data?.pages.flatMap(page => page.owners) ?? [];

  return { handleLoadMore, handleSearchChange, isFetching, owners };
};
